"use client"

import { useEffect } from "react"
import { $insertDataTransferForRichText } from "@lexical/clipboard"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { $getSelection, $isRangeSelection, COMMAND_PRIORITY_HIGH, PASTE_COMMAND } from "lexical"

const INTERACTIVE_TEXT_TAGS = "input,textarea,select,option,button,output,meter,progress"

const INVISIBLE_CHAR_RE = /[\u200B\uFEFF]/g

const DIGIT_RUN_RE = /\d+/g

function inlineTextFromInteractiveElements(doc: Document): boolean {
  const elements = doc.body.querySelectorAll(INTERACTIVE_TEXT_TAGS)
  if (elements.length === 0) return false

  elements.forEach((el) => {
    const text = el instanceof HTMLInputElement ? el.value || el.getAttribute("value") || "" : (el.textContent ?? "")
    if (text) {
      el.replaceWith(doc.createTextNode(text))
    } else {
      el.remove()
    }
  })
  return true
}

function unwrapNamespacedElements(doc: Document): boolean {
  const namespaced = Array.from(doc.body.querySelectorAll("*")).filter((el) => el.tagName.includes(":"))
  if (namespaced.length === 0) return false

  for (const el of namespaced) {
    const text = el.textContent ?? ""
    if (text) {
      el.replaceWith(doc.createTextNode(text))
    } else {
      el.remove()
    }
  }
  return true
}

function stripInvisibleCharacters(doc: Document): boolean {
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT)
  const updates: { node: Text; value: string }[] = []
  let current: Node | null = walker.nextNode()
  while (current) {
    const tn = current as Text
    const cleaned = (tn.nodeValue || "").replace(INVISIBLE_CHAR_RE, "")
    if (cleaned !== tn.nodeValue) {
      updates.push({ node: tn, value: cleaned })
    }
    current = walker.nextNode()
  }
  if (updates.length === 0) return false
  for (const { node, value } of updates) {
    node.nodeValue = value
  }
  return true
}

function digitRunMultiset(text: string): Map<string, number> {
  const counts = new Map<string, number>()
  const matches = text.match(DIGIT_RUN_RE)
  if (!matches) return counts
  for (const run of matches) {
    counts.set(run, (counts.get(run) ?? 0) + 1)
  }
  return counts
}

function htmlPreservesPlainTextDigits(htmlText: string, plain: string): boolean {
  const plainCounts = digitRunMultiset(plain)
  if (plainCounts.size === 0) return true
  const htmlCounts = digitRunMultiset(htmlText)
  for (const [run, count] of plainCounts) {
    if ((htmlCounts.get(run) ?? 0) < count) return false
  }
  return true
}

interface NormalizedPaste {
  data: DataTransfer
  changed: boolean
}

function buildSyntheticDataTransfer(
  dt: DataTransfer,
  overrideHtml: string | null,
  preservePlainOnly: boolean
): DataTransfer {
  const synthetic = new DataTransfer()
  const plain = dt.getData("text/plain")
  const lex = dt.getData("application/x-lexical-editor")
  if (!preservePlainOnly && overrideHtml !== null) {
    synthetic.setData("text/html", overrideHtml)
  }
  if (plain) synthetic.setData("text/plain", plain)
  if (lex) synthetic.setData("application/x-lexical-editor", lex)
  return synthetic
}

function normalizeClipboard(dt: DataTransfer): NormalizedPaste | null {
  if (typeof DOMParser === "undefined") return null

  const html = dt.getData("text/html")
  if (!html) return null

  const doc = new DOMParser().parseFromString(html, "text/html")

  const interactiveMutated = inlineTextFromInteractiveElements(doc)
  const namespacedMutated = unwrapNamespacedElements(doc)
  const invisibleMutated = stripInvisibleCharacters(doc)
  const htmlChanged = interactiveMutated || namespacedMutated || invisibleMutated

  const normalizedHtml = htmlChanged ? doc.body.innerHTML : html

  const plain = dt.getData("text/plain")
  const htmlPreservesDigits = plain ? htmlPreservesPlainTextDigits(doc.body.textContent ?? "", plain) : true

  if (!htmlChanged && htmlPreservesDigits) return null

  return {
    data: buildSyntheticDataTransfer(dt, normalizedHtml, !htmlPreservesDigits),
    changed: true,
  }
}

export default function PasteNormalizePlugin(): null {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      (event) => {
        if (!(event instanceof ClipboardEvent)) return false
        const dt = event.clipboardData
        if (!dt) return false

        const normalized = normalizeClipboard(dt)
        if (!normalized) return false

        const selection = $getSelection()
        if (!$isRangeSelection(selection)) return false

        event.preventDefault()
        $insertDataTransferForRichText(normalized.data, selection, editor)
        return true
      },
      COMMAND_PRIORITY_HIGH
    )
  }, [editor])

  return null
}
